import { useCallback, useState } from "react";
import uniqueId from "lodash/uniqueId";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useInvitationStart } from "@vega-api/generated/auth-invitation-v1/auth-invitation-v1";
import { useWorkbenchWorkbenchInvitationStart } from "@vega-api/generated/auth-invitation-workbench-workbench-v1/auth-invitation-workbench-workbench-v1";
import { type InvitationPermissionEntry } from "@vega-api/generated/types/invitationPermissionEntry";
import { type InvitationStartRequest } from "@vega-api/generated/types/invitationStartRequest";
import { type PermissionEntry } from "@vega-api/generated/types/permissionEntry";
import { type PortalInvitationStartRequest } from "@vega-api/generated/types/portalInvitationStartRequest";
import { type BaseMultiSelectOption } from "@vega-core/components/Form/Controls/Dropdown/ThemedMultiselectDropdown/ThemedMultiselectDropdown";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import {
  OnboardingInviteType,
  trackInviteCreateAttempted,
  trackInviteCreateFailed,
} from "@vega-core/utils/posthog/onboardingAnalytics";

import {
  type CollaboratorsV2State,
  FUND_VEHICLE_TAB,
  FUND_VEHICLE_TAB_TO_SUBJECT_TYPE,
  type FundVehicleTab,
} from "../components/collaboratorsV2Constants";

const getInitialCollaboratorState = (index: number): CollaboratorsV2State => ({
  id: uniqueId("accordion-"),
  index,
  selectedRole: {
    label: "",
    value: "",
  },
  fundVehicleTab: FUND_VEHICLE_TAB.ALL,
});

export const useCollaboratorsV2 = (setOpen: (open: boolean) => void) => {
  const [collaboratorsAccordions, setCollaboratorsAccordions] = useState<
    CollaboratorsV2State[]
  >([getInitialCollaboratorState(1)]);

  const showToast = useToast();
  const { featureFlag } = useFeatureFlag();

  const isInvitationV2 = featureFlag[FeatureFlag.GP_AUTH_INVITATION_V2];

  const invitationMutationOptions = {
    mutation: {
      onSuccess: () => {
        showToast({
          title: m.userManagement_invite_success(),
          variant: "success",
        });
        handleClose();
      },
      onError: (error: ErrorType<unknown>) => {
        trackInviteCreateFailed({
          inviteType: OnboardingInviteType.GP_WORKBENCH,
          roleCount: collaboratorsAccordions.length,
          error,
        });

        const { title, description } = getErrorMessage(error, {
          defaultTitle: m.userManagement_invite_error(),
        });
        showToast({
          title,
          description,
          variant: "destructive",
        });
      },
    },
  };

  const legacyInvitation = useInvitationStart(invitationMutationOptions);
  const workbenchWorkbenchInvitation = useWorkbenchWorkbenchInvitationStart(
    invitationMutationOptions,
  );

  const isPending = isInvitationV2
    ? workbenchWorkbenchInvitation.isPending
    : legacyInvitation.isPending;

  const resetCollaborators = () => {
    setCollaboratorsAccordions([getInitialCollaboratorState(1)]);
  };

  const handleClose = useCallback(() => {
    resetCollaborators();
    setOpen(false);
  }, [setOpen]);

  const updateAccordion = (
    id: string,
    updater: (accordion: CollaboratorsV2State) => CollaboratorsV2State,
  ) => {
    setCollaboratorsAccordions((prev) =>
      prev.map((accordion) =>
        accordion.id === id ? updater(accordion) : accordion,
      ),
    );
  };

  const handleRoleChange = (
    id: string,
    newSelectedRole: BaseMultiSelectOption,
  ) => {
    updateAccordion(id, (accordion) => ({
      ...accordion,
      selectedRole: newSelectedRole,
    }));
  };

  const handleFundVehicleTabChange = (id: string, tab: FundVehicleTab) => {
    updateAccordion(id, (accordion) => ({
      ...accordion,
      fundVehicleTab: tab,
    }));
  };

  const deleteCollaboratorsAccordion = (id: string) => {
    setCollaboratorsAccordions((prev) =>
      prev
        .filter((accordion) => accordion.id !== id)
        .map((accordion, newIndex) => ({
          ...accordion,
          index: newIndex + 1,
        })),
    );
  };

  const handleAddAccordion = () => {
    const newAccordion = getInitialCollaboratorState(
      collaboratorsAccordions.length + 1,
    );
    setCollaboratorsAccordions((prev) => [...prev, newAccordion]);
  };

  const isValid = () => {
    if (collaboratorsAccordions.length === 0) return false;

    return collaboratorsAccordions.every(
      (accordion) => !!accordion.selectedRole.value,
    );
  };

  const handleInviteCollaborators = useCallback(
    (values: { email: string }) => {
      trackInviteCreateAttempted({
        inviteType: OnboardingInviteType.GP_WORKBENCH,
        roleCount: collaboratorsAccordions.length,
      });

      // --- New portal-pair API (workbench-workbench) ---
      if (isInvitationV2) {
        const entries: InvitationPermissionEntry[] =
          collaboratorsAccordions.map((accordion) => ({
            roleId: accordion.selectedRole.value,
            subjectType:
              FUND_VEHICLE_TAB_TO_SUBJECT_TYPE[accordion.fundVehicleTab],
          }));

        const payload: PortalInvitationStartRequest = {
          email: values.email,
          permissions: { entries },
        };

        workbenchWorkbenchInvitation.mutate({ data: payload });

        return;
      }

      // --- Legacy (auth-invitation-v1). Delete this branch once
      // gp_auth_invitation_v2 is fully rolled out. ---
      const entries: PermissionEntry[] = collaboratorsAccordions.map(
        (accordion) => ({
          subjectId: undefined,
          roleId: accordion.selectedRole.value,
          subjectType:
            FUND_VEHICLE_TAB_TO_SUBJECT_TYPE[accordion.fundVehicleTab],
        }),
      );

      const payload: InvitationStartRequest = {
        email: values.email,
        permissions: { entries },
      };

      legacyInvitation.mutate({ data: payload });
    },
    [
      isInvitationV2,
      workbenchWorkbenchInvitation,
      legacyInvitation,
      collaboratorsAccordions,
    ],
  );

  return {
    collaboratorsAccordions,
    handleFundVehicleTabChange,
    handleRoleChange,
    deleteCollaboratorsAccordion,
    handleAddAccordion,
    isValid,
    handleInviteCollaborators,
    isPending,
    handleClose,
  };
};
